import { createElement, useCallback } from 'react';
import { defineMessages } from 'react-intl';

import type { AiSuggestionsRightRailEntryPoint } from '@atlaskit/editor-common/analytics';

import {
	useObjectSidebarActions,
	usePanel,
	useIsObjectSidebarShown,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { PanelName } from '@confluence/object-sidebar-api/entry-points/PanelName.ts';

import { SuggestionsPanelLayout } from './components/SuggestionsPanelLayout.tsx';
import { SuggestionsCount } from './components/SuggestionsHeader.tsx';
import { SuggestionsHeaderActionGroup } from './components/SuggestionsHeaderActionGroup.tsx';

const i18n = defineMessages({
	suggestionsPanelHeaderLabel: {
		id: 'suggestions-panel.panel.header.review-suggestions.label',
		defaultMessage: 'Review suggestions',
		description:
			'The text is shown as the header label at the top of the AI suggestions sidebar panel, identifying the panel to the user.',
	},
});

export const useShowSuggestionsPanel = (): {
	isSuggestionsPanelShown: boolean;
	showSuggestionsPanel: (options?: { entryPoint?: AiSuggestionsRightRailEntryPoint }) => void;
	hideSuggestionsPanel: (afterCloseCallback?: () => void) => Promise<void>;
} => {
	const isObjectSidebarShown = useIsObjectSidebarShown();
	const panel = usePanel();
	const { showObjectSidebar, hideObjectSidebar, changePanel } = useObjectSidebarActions();

	const showSuggestionsPanel = useCallback(
		({ entryPoint }: { entryPoint?: AiSuggestionsRightRailEntryPoint } = {}): void => {
			const suggestionsPanel = {
				id: PanelName.SuggestionsPanel,
				headerComponentElements: {
					headerLabel: i18n.suggestionsPanelHeaderLabel,
					HeaderAfterIconElement: SuggestionsCount,
					// Renders the more menu followed by the filter menu; the panel header appends its
					// close button after this element, so the filter button sits between the two.
					HeaderRightAlignedElement: SuggestionsHeaderActionGroup,
				},
				BodyComponent: () => createElement(SuggestionsPanelLayout, { entryPoint }),
			};

			if (isObjectSidebarShown) {
				void changePanel(suggestionsPanel, 'push');
				return;
			}

			showObjectSidebar(suggestionsPanel, 'push');
		},
		[changePanel, isObjectSidebarShown, showObjectSidebar],
	);

	return {
		isSuggestionsPanelShown: isObjectSidebarShown && panel?.id === PanelName.SuggestionsPanel,
		showSuggestionsPanel,
		hideSuggestionsPanel: hideObjectSidebar,
	};
};
